import { useEffect, useState } from "react";
import api from "@/lib/api";
import DataTable from "@/components/admin/DataTable";
import SearchBox from "@/components/admin/SearchBox";
import EmptyState from "@/components/admin/EmptyState";
import ExportCSVButton from "@/components/admin/ExportCSVButton";
import { Users } from "lucide-react";
import AdminCustomerDetail from "./AdminCustomerDetail";

function relTime(iso) {
  if (!iso) return "never";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-AU");
}

function money(n) {
  return `$${(n ?? 0).toFixed(2)}`;
}

const COLS = [
  { key: "name",  label: "Name", sortable: false, render: (r) => (
      <span className="flex items-center gap-2">
        {r.name || "—"}
        {r.is_internal && (
          <span data-testid={`customer-internal-${r.id}`} className="inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-widest bg-stone-100 text-stone-700 border border-stone-200">
            Internal
          </span>
        )}
      </span>
  )},
  { key: "email", label: "Email", sortable: false, render: (r) => (
      <span className="font-mono text-xs">{r.email}</span>
  )},
  { key: "projects_count", label: "Projects", sortable: false, className: "text-right" },
  { key: "spend",          label: "Spend", sortable: true, className: "text-right",
    render: (r) => money(r.spend_aud) },
  { key: "last_login",     label: "Last login", sortable: true,
    render: (r) => relTime(r.last_login_at) },
];

export default function AdminCustomers() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [q, setQ] = useState("");
  const [hasPaid, setHasPaid] = useState("all");
  const [hasProjects, setHasProjects] = useState("all");
  const [sort, setSort] = useState("last_login");
  const [order, setOrder] = useState("desc");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (selectedId) return; // skip list refetch while viewing detail
    setLoading(true);
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      q,
      has_paid: hasPaid,
      has_projects: hasProjects,
      sort,
      order,
    });
    api.get(`/admin/customers?${params.toString()}`)
      .then((r) => { setData(r.data); setErr(""); })
      .catch((e) => setErr(e.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [page, pageSize, q, hasPaid, hasProjects, sort, order, selectedId]);

  if (selectedId) {
    return (
      <AdminCustomerDetail
        customerId={selectedId}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  return (
    <div data-testid="admin-customers">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
        <div>
          <h2 className="font-serif text-3xl text-brand-ink tracking-tight">Customers</h2>
          <div className="text-xs text-brand-muted mt-1">
            {data.total} total · {data.internal_user_count ?? 0} internal (owner/admin) excluded
          </div>
        </div>
        <ExportCSVButton
          path="/admin/customers/export.csv"
          params={{ q }}
          filename="colourmatch-customers.csv"
          testId="customers-export-csv"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <SearchBox
          value={q}
          onChange={(v) => { setQ(v); setPage(1); }}
          placeholder="Search name or email…"
          testId="customers-search"
        />

        <label className="text-sm text-brand-muted flex items-center gap-2">
          Has paid
          <select
            data-testid="customers-filter-has-paid"
            value={hasPaid}
            onChange={(e) => { setHasPaid(e.target.value); setPage(1); }}
            className="border border-brand-linen bg-white px-2 py-1 text-sm"
          >
            <option value="all">All</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </label>

        <label className="text-sm text-brand-muted flex items-center gap-2">
          Has projects
          <select
            data-testid="customers-filter-has-projects"
            value={hasProjects}
            onChange={(e) => { setHasProjects(e.target.value); setPage(1); }}
            className="border border-brand-linen bg-white px-2 py-1 text-sm"
          >
            <option value="all">All</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </label>

        <label className="text-sm text-brand-muted flex items-center gap-2">
          Sort
          <select
            data-testid="customers-sort"
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
            className="border border-brand-linen bg-white px-2 py-1 text-sm"
          >
            <option value="last_login">Last login</option>
            <option value="created">Created</option>
            <option value="spend">Spend</option>
          </select>
        </label>
      </div>

      {err && (
        <div className="text-sm border border-destructive/30 bg-destructive/5 text-destructive px-4 py-3 mb-4">
          {err}
        </div>
      )}

      <DataTable
        testId="customers-table"
        columns={COLS}
        rows={data.items}
        total={data.total}
        page={page}
        pageSize={pageSize}
        sortKey={sort === "spend" || sort === "last_login" ? sort : null}
        sortOrder={order}
        onSortChange={(k, o) => { setSort(k); setOrder(o); setPage(1); }}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        onRowClick={(row) => setSelectedId(row.id)}
        loading={loading}
        empty={(
          <EmptyState
            icon={Users}
            title="No customers match these filters"
            caption="Try clearing the search or changing the filters."
            action={
              <button
                data-testid="customers-empty-clear"
                onClick={() => { setQ(""); setHasPaid("all"); setHasProjects("all"); setPage(1); }}
                className="text-sm border border-brand-teal px-4 py-2 text-brand-teal hover:bg-brand-teal hover:text-white transition-colors"
              >
                Clear filters
              </button>
            }
          />
        )}
      />
    </div>
  );
}
